import React, { FC, useEffect, useState } from 'react';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Spin, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { UploadProps } from 'antd/es/upload/interface';
import 'antd/lib/modal/style/index';
import 'antd/lib/slider/style/index';
import { getBase64 } from 'tools/base64';
import { t } from 'tools/i18n';
import { Icon, Typography, message } from 'ui';

import { useDeleteLogoMutation, useUploadLogoMutation } from 'services/promoter/promoterApiService';
import { setActivePromoterFullInfo, setEditingPromoter } from 'slices/systemSlice';
import { useAppDispatch } from 'store';

import { PromoterFullInfoType } from 'types/promoters/promoterTypes';

import s from './UploadLogo.module.scss';

const { Dragger } = Upload;

interface UploadLogoProps {
  promoterData: PromoterFullInfoType;
}

const UploadLogo: FC<UploadLogoProps> = ({ promoterData }) => {
  const [imageUrl, setImageUrl] = useState<string>();
  const [uploadLogo, { isLoading: uploading }] = useUploadLogoMutation();
  const [deleteLogo, { isLoading: deleting }] = useDeleteLogoMutation();
  const dispatch = useAppDispatch();
  const [logoUrl, setLogoUrl] = useState<string | undefined>();

  const props: UploadProps = {
    onRemove: () => {
      setImageUrl('');
    },
    beforeUpload: (file) => {
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isLt2M) {
        message.error('File must be smaller than 2MB!');
        return Upload.LIST_IGNORE; // prevent file from being added to list
      }

      getBase64(file, (url) => {
        setImageUrl(url);
      });

      return false;
    },
    multiple: false,
    maxCount: 1,
    showUploadList: false,
  };

  const handleDeleteLogo = async () => {
    if (!promoterData?.id) return;

    try {
      await deleteLogo({ promoterId: promoterData?.id }).unwrap(); // assumes `deleteLogo(id)` signature
      setImageUrl('');
      setLogoUrl(undefined);
      message.success('Profile picture deleted successfully');
      dispatch(setEditingPromoter(true)); // mark as changed if needed
    } catch (err) {
      console.error('Failed to delete logo:', err);
      // Optionally show error notification
    }
  };

  useEffect(() => {
    if (imageUrl || promoterData?.logo) {
      setLogoUrl(imageUrl || promoterData?.logo);
    } else {
      setLogoUrl(undefined);
    }
  }, [imageUrl, promoterData, setLogoUrl]);

  useEffect(() => {
    const uploadProfileLogo = async () => {
      if (!imageUrl || !promoterData?.id) return;

      try {
        const res = await uploadLogo({ logo: imageUrl, promoterId: promoterData.id });

        if ('data' in res) {
          dispatch(setActivePromoterFullInfo(res?.data[0]));
          dispatch(setEditingPromoter(true));
          setLogoUrl(res?.data[0].logo);
          setImageUrl('');
          message.success('Profile picture uploaded successfully');
        }

        // Debug log
        // console.log('Upload result:', res);
      } catch (error) {
        console.error('Failed to upload logo:', error);
        message.error('Failed to upload profile picture');
      }
    };

    uploadProfileLogo();
  }, [imageUrl, promoterData?.id, dispatch, uploadLogo]);

  return (
    <div>
      <ImgCrop
        rotationSlider
        modalProps={{ okButtonProps: { block: true }, cancelButtonProps: { block: true } }}>
        <Dragger disabled={deleting} {...props} className={s.dragger}>
          {logoUrl ? (
            <div className={s.imageWrapper}>
              {uploading || deleting ? (
                <div className={s.loadingOverlay}>
                  <Spin />
                </div>
              ) : (
                <img src={`${logoUrl}?t=${Date.now()}`} alt="logo" className={s.image} />
              )}

              {!uploading && !deleting && (
                <div className={s.actions}>
                  <button
                    type="button"
                    onClick={() => {
                      setImageUrl('');
                    }}
                    className={s.update}>
                    <EditOutlined /> Update
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLogo();
                    }}
                    className={s.delete}>
                    <DeleteOutlined /> Delete
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={s.hint}>
              <Icon name="thumbnail" size={40} />
              <Typography type="small" className={s.uploadHint}>
                {t('settings_upload_logo')}
              </Typography>
              <Typography type="small" className={s.uploadHintType}>
                {t('setting_upload_logo_type')}
              </Typography>
            </div>
          )}
        </Dragger>
      </ImgCrop>
    </div>
  );
};

export default UploadLogo;
